import { TRANSFER_STATES } from '../constant/transfer-states';
import TransferItem from '../models/transfer-item';
import {
  DownloadTransferResponseRaw,
  OwnedTransferResponseRaw,
  NormalizedTransfer,
  NormalizedItem,
  OwnedTransferItem,
  UploadConfiguration,
} from '../types/transfers';
import * as TransferItemsHelper from '@wetransfer/transfer/helper/transfer-items';

function isTransferItem(
  transfer: TransferItem | OwnedTransferItem
): transfer is TransferItem {
  return (transfer as TransferItem).apiType !== undefined;
}

export function normalizeItem(
  file: TransferItem | OwnedTransferItem
): NormalizedItem {
  file.apiType = isTransferItem(file) ? file.apiType : file.item_type;

  return {
    id: file.id,
    content_identifier: 'file',
    name: file.name,
    size: file.size,
    downloadable: true,
    previewable: file.previewable,
    apiType: file.apiType,
    file: new TransferItem(file as unknown as File),
  };
}

export function normalizeTransfer(
  transfer: OwnedTransferResponseRaw & DownloadTransferResponseRaw
): NormalizedTransfer {
  const newTransfer = {} as NormalizedTransfer;

  // Ensure consistent usage of expiresAt and expiryInSeconds naming
  if (
    (transfer.expires_at && typeof transfer.expires_at === 'string') ||
    typeof transfer.expires_at === 'object'
  ) {
    newTransfer.expiresAt = transfer.expires_at;
  }

  if (typeof transfer.expiry_in_seconds === 'number') {
    newTransfer.expiryInSeconds = transfer.expiry_in_seconds;
  }

  newTransfer.description = transfer.description || transfer.message;

  const files = transfer.files || transfer.items || []; // Let's save the files array

  if (
    transfer.state === TRANSFER_STATES.INIT ||
    (transfer.files && !transfer.items)
  ) {
    newTransfer.items = files.map(normalizeItem);
  }

  newTransfer.size =
    transfer.size || files.reduce((total, { size = 0 }) => (total += size), 0);

  newTransfer.items = TransferItemsHelper.getItemsWithUITypes(
    newTransfer.items || transfer.items
  );

  newTransfer.itemsTree = TransferItemsHelper.buildItemsTree(newTransfer.items);
  newTransfer.files = files as unknown as TransferItem[];

  newTransfer.title = transfer.displayName;

  /*
   * items (and files) can be empty when this function is called from the
   * Files#render (components/transfer/uploader/files.js)
   * this is also the case if the transfer is password protected
   * */
  if (!newTransfer.title && newTransfer.items.length) {
    newTransfer.title = TransferItemsHelper.getSubNodesFromItemName(
      newTransfer.items[0].name
    ).pop();
  }

  newTransfer.creator = {
    auth0UserId: transfer.creator?.auth0_user_id,
    email: transfer.creator?.email,
  };

  return { ...transfer, ...newTransfer } as NormalizedTransfer;
}

export function prepareTransfer({
  files,
  forwardedItems,
  uploadConfig,
  userInfo,
  parentId,
}: {
  files: File[];
  forwardedItems?: TransferItem[];
  uploadConfig: UploadConfiguration;
  userInfo: {
    language: string;
    sender: string;
    userType: string;
    accountId: number;
    domainUserId?: string;
    channel?: string;
  };
  parentId: string | boolean;
}): NormalizedTransfer {
  const itemsTree = TransferItemsHelper.buildItemsTree(files);
  const items = forwardedItems || files.map((file) => new TransferItem(file));

  const title = TransferItemsHelper.getSubNodesFromItemName(
    items[0].name
  ).pop();
  const size = items.reduce((total, { size }) => (total += size), 0);

  return {
    id: false,
    title,
    files: items,
    items: items.map(normalizeItem),
    itemsTree,
    state: TRANSFER_STATES.INIT,
    size,
    description: uploadConfig.message,
    password: uploadConfig.password,
    message: uploadConfig.message,
    recipients: uploadConfig.recipients,
    type: uploadConfig.type,
    expiry: uploadConfig.expiryDate,
    language: userInfo.language,
    sender: userInfo.sender,
    userType: userInfo.userType,
    accountId: userInfo.accountId,
    parent_id: parentId,
    paid: false,
  };
}
